"use client";

import type { ReactNode, RefObject } from "react";
import {
  clamp,
  DiagramFrame,
  fitBox,
  Guide,
  Label,
  SHAPE,
} from "@/components/tools/geometry/diagram";
import type { SolidFieldId, SolidId } from "@/lib/tool-kit/geometry/solids";

/*
 * Labelled figures of the solids, in oblique projection (boxes, prisms, pyramids) or with
 * elliptical rims (cylinders, cones, spheres). Hidden edges are dashed. Proportions
 * follow the numbers typed, kept within a readable range.
 */

export const SOLID_SYMBOLS: Record<SolidFieldId, string> = {
  side: "a",
  length: "l",
  width: "w",
  height: "h",
  radius: "r",
  sideA: "a",
  sideB: "b",
  sideC: "c",
  prismLength: "L",
  bottomRadius: "R",
  topRadius: "r",
  semiA: "a",
  semiB: "b",
  semiC: "c",
};

export interface SolidDiagramProps {
  solid: SolidId;
  title: string;
  svgRef: RefObject<SVGSVGElement | null>;
  /** Each measurement with its unit: "6 cm". */
  labels: Partial<Record<SolidFieldId, string>>;
  /** The same measurements as numbers in one unit, for proportions. */
  values: Partial<Record<SolidFieldId, number>>;
  /** A slant height to draw and label ("l = 5 cm"), for cones, frusta and pyramids. */
  slantLabel?: string;
}

interface DrawProps {
  label: (id: SolidFieldId) => string;
  value: (id: SolidFieldId) => number;
  slantLabel?: string;
}

interface Point {
  x: number;
  y: number;
}

const CX = 150;
const BOTTOM = 196;
const DEPTH_X = 0.4;
const DEPTH_Y = 0.3;
const RIM = 0.3;
const FILL = SHAPE.fill;
const FILL_OPACITY = SHAPE.fillOpacity;

const pt = (p: Point) => `${p.x.toFixed(2)} ${p.y.toFixed(2)}`;
const poly = (points: Point[]) => `M${points.map(pt).join(" L")} Z`;

function Face({ points, shade = 1 }: { points: Point[]; shade?: number }) {
  return (
    <path
      d={poly(points)}
      fill={FILL}
      fillOpacity={FILL_OPACITY * shade}
      stroke={SHAPE.stroke}
      strokeLinejoin="round"
      strokeWidth={2.2}
    />
  );
}

function Hidden({ from, to }: { from: Point; to: Point }) {
  return (
    <line
      stroke={SHAPE.muted}
      strokeDasharray="4 4"
      strokeWidth={1.4}
      x1={from.x}
      x2={to.x}
      y1={from.y}
      y2={to.y}
    />
  );
}

function Edge({ from, to }: { from: Point; to: Point }) {
  return (
    <line
      stroke={SHAPE.stroke}
      strokeWidth={2.2}
      x1={from.x}
      x2={to.x}
      y1={from.y}
      y2={to.y}
    />
  );
}

/** A slant height drawn as a dashed accent line with its label beside the midpoint. */
function Slant({ from, to, text }: { from: Point; to: Point; text?: string }) {
  if (!text) {
    return null;
  }
  return (
    <>
      <Guide color={SHAPE.accent} x1={from.x} x2={to.x} y1={from.y} y2={to.y} />
      <Label
        anchor="start"
        color={SHAPE.ink}
        x={(from.x + to.x) / 2 + 8}
        y={(from.y + to.y) / 2 + 4}
      >
        {text}
      </Label>
    </>
  );
}

/** Values scaled so the largest is 1 and none is thinner than a quarter of it. */
function proportions(values: number[]): number[] {
  const top = Math.max(...values, Number.MIN_VALUE);
  return values.map((v) => clamp(v / top, 0.25, 1));
}

function Box({ label, value, cube }: DrawProps & { cube?: boolean }) {
  const [l, w, h] = cube
    ? [1, 1, 1]
    : proportions([value("length"), value("width"), value("height")]);
  const k = Math.min(180 / (l + DEPTH_X * w), 130 / (h + DEPTH_Y * w));
  const fw = l * k;
  const fh = h * k;
  const ox = DEPTH_X * w * k;
  const oy = DEPTH_Y * w * k;
  const x0 = CX - (fw + ox) / 2;
  const yb = BOTTOM;
  const yt = yb - fh;
  const ftl = { x: x0, y: yt };
  const ftr = { x: x0 + fw, y: yt };
  const fbr = { x: x0 + fw, y: yb };
  const fbl = { x: x0, y: yb };
  const btl = { x: x0 + ox, y: yt - oy };
  const btr = { x: x0 + fw + ox, y: yt - oy };
  const bbr = { x: x0 + fw + ox, y: yb - oy };
  const bbl = { x: x0 + ox, y: yb - oy };
  const lengthId = cube ? "side" : "length";
  return (
    <>
      <Hidden from={bbl} to={fbl} />
      <Hidden from={bbl} to={bbr} />
      <Hidden from={bbl} to={btl} />
      <Face points={[fbl, fbr, ftr, ftl]} />
      <Face points={[ftl, ftr, btr, btl]} shade={0.7} />
      <Face points={[ftr, fbr, bbr, btr]} shade={1.6} />
      <Label x={x0 + fw / 2} y={yb + 20}>
        {label(lengthId)}
      </Label>
      {cube ? null : (
        <>
          <Label anchor="end" x={x0 - 8} y={yb - fh / 2 + 4}>
            {label("height")}
          </Label>
          <Label
            anchor="start"
            x={(fbr.x + bbr.x) / 2 + 8}
            y={(fbr.y + bbr.y) / 2 + 16}
          >
            {label("width")}
          </Label>
        </>
      )}
    </>
  );
}

function TriPrism({ label, value }: DrawProps) {
  const a = value("sideA");
  const b = value("sideB");
  const c = value("sideC");
  const along = (b * b + c * c - a * a) / (2 * c);
  const rise = Math.sqrt(Math.max(b * b - along * along, 0));
  const depth = clamp(value("prismLength") / Math.max(a, b, c), 0.3, 2.2) * c;
  const minX = Math.min(0, along);
  const maxX = Math.max(c, along);
  const k = Math.min(
    170 / (maxX - minX + DEPTH_X * depth || 1),
    125 / (rise + DEPTH_Y * depth || 1)
  );
  const ox = DEPTH_X * depth * k;
  const oy = DEPTH_Y * depth * k;
  const left = CX - ((maxX - minX) * k + ox) / 2;
  const at = (x: number, y: number): Point => ({
    x: left + (x - minX) * k,
    y: BOTTOM - y * k,
  });
  const pa = at(0, 0);
  const pb = at(c, 0);
  const pc = at(along, rise);
  const shift = (p: Point): Point => ({ x: p.x + ox, y: p.y - oy });
  const [qa, qb, qc] = [shift(pa), shift(pb), shift(pc)];
  return (
    <>
      <Hidden from={qa} to={pa} />
      <Hidden from={qa} to={qb} />
      <Hidden from={qa} to={qc} />
      <Face points={[pb, qb, qc, pc]} shade={1.6} />
      <Face points={[pc, qc, qa, pa]} shade={0.5} />
      <Face points={[pa, pb, pc]} />
      <Label x={(pa.x + pb.x) / 2} y={BOTTOM + 20}>
        {label("sideC")}
      </Label>
      <Label anchor="end" x={(pa.x + pc.x) / 2 - 8} y={(pa.y + pc.y) / 2}>
        {label("sideB")}
      </Label>
      <Label
        anchor="start"
        x={(pb.x + qb.x) / 2 + 8}
        y={(pb.y + qb.y) / 2 + 16}
      >
        {label("prismLength")}
      </Label>
      <Label
        anchor="start"
        x={(pb.x + pc.x) / 2 + 22}
        y={(pb.y + pc.y) / 2 - 6}
      >
        {label("sideA")}
      </Label>
    </>
  );
}

function rimPath(
  cx: number,
  y: number,
  rx: number,
  ry: number,
  front: boolean
) {
  return `M${cx - rx} ${y} A${rx} ${ry} 0 0 ${front ? 0 : 1} ${cx + rx} ${y}`;
}

function Rim({
  cx,
  y,
  rx,
  ry,
}: {
  cx: number;
  y: number;
  rx: number;
  ry: number;
}) {
  return (
    <>
      <path
        d={rimPath(cx, y, rx, ry, false)}
        fill="none"
        stroke={SHAPE.muted}
        strokeDasharray="4 4"
        strokeWidth={1.4}
      />
      <path
        d={rimPath(cx, y, rx, ry, true)}
        fill="none"
        stroke={SHAPE.stroke}
        strokeWidth={2.2}
      />
    </>
  );
}

function roundSize(radius: number, height: number, maxW = 170, maxH = 120) {
  const { w, h } = fitBox((2 * radius) / height, maxW, maxH);
  const rx = w / 2;
  return { rx, ry: rx * RIM, h };
}

function Cylinder({ label, value }: DrawProps) {
  const { rx, ry, h } = roundSize(value("radius"), value("height"));
  const top = BOTTOM - ry - h;
  const bottom = top + h;
  return (
    <>
      <path
        d={`M${CX - rx} ${top} L${CX - rx} ${bottom} A${rx} ${ry} 0 0 0 ${CX + rx} ${bottom} L${CX + rx} ${top} Z`}
        fill={FILL}
        fillOpacity={FILL_OPACITY}
      />
      <Edge from={{ x: CX - rx, y: top }} to={{ x: CX - rx, y: bottom }} />
      <Edge from={{ x: CX + rx, y: top }} to={{ x: CX + rx, y: bottom }} />
      <Rim cx={CX} rx={rx} ry={ry} y={bottom} />
      <ellipse
        cx={CX}
        cy={top}
        fill={FILL}
        fillOpacity={FILL_OPACITY * 1.5}
        rx={rx}
        ry={ry}
        stroke={SHAPE.stroke}
        strokeWidth={2.2}
      />
      <Guide x1={CX} x2={CX + rx} y1={top} y2={top} />
      <Label x={CX + rx / 2} y={top - ry - 6}>
        {label("radius")}
      </Label>
      <Label anchor="start" x={CX + rx + 10} y={(top + bottom) / 2 + 4}>
        {label("height")}
      </Label>
    </>
  );
}

function Cone({ label, value, slantLabel }: DrawProps) {
  const { rx, ry, h } = roundSize(value("radius"), value("height"), 170, 125);
  const base = BOTTOM - ry;
  const apex = { x: CX, y: base - h };
  const right = { x: CX + rx, y: base };
  return (
    <>
      <path
        d={`M${CX - rx} ${base} L${apex.x} ${apex.y} L${CX + rx} ${base} A${rx} ${ry} 0 0 1 ${CX - rx} ${base} Z`}
        fill={FILL}
        fillOpacity={FILL_OPACITY}
      />
      <Edge from={{ x: CX - rx, y: base }} to={apex} />
      <Edge from={right} to={apex} />
      <Rim cx={CX} rx={rx} ry={ry} y={base} />
      <Guide x1={CX} x2={CX} y1={apex.y} y2={base} />
      <Guide x1={CX} x2={CX + rx} y1={base} y2={base} />
      <Label anchor="end" x={CX - rx / 2 - 10} y={(apex.y + base) / 2 + 4}>
        {label("height")}
      </Label>
      <Label x={CX + rx / 2} y={base + ry + 16}>
        {label("radius")}
      </Label>
      <Slant from={right} text={slantLabel} to={apex} />
    </>
  );
}

function Sphere({ label }: DrawProps) {
  const radius = 76;
  const cy = 112;
  return (
    <>
      <circle
        cx={CX}
        cy={cy}
        fill={FILL}
        fillOpacity={FILL_OPACITY}
        r={radius}
        stroke={SHAPE.stroke}
        strokeWidth={2.2}
      />
      <Rim cx={CX} rx={radius} ry={radius * 0.28} y={cy} />
      <Guide x1={CX} x2={CX + radius} y1={cy} y2={cy} />
      <circle cx={CX} cy={cy} fill={SHAPE.guide} r={3} />
      <Label anchor="start" x={CX + radius + 8} y={cy + 4}>
        {label("radius")}
      </Label>
    </>
  );
}

function Hemisphere({ label }: DrawProps) {
  const radius = 84;
  const ry = radius * 0.3;
  const base = 150;
  return (
    <>
      <path
        d={`M${CX - radius} ${base} A${radius} ${radius} 0 0 1 ${CX + radius} ${base} A${radius} ${ry} 0 0 1 ${CX - radius} ${base} Z`}
        fill={FILL}
        fillOpacity={FILL_OPACITY}
      />
      <path
        d={`M${CX - radius} ${base} A${radius} ${radius} 0 0 1 ${CX + radius} ${base}`}
        fill="none"
        stroke={SHAPE.stroke}
        strokeWidth={2.2}
      />
      <Rim cx={CX} rx={radius} ry={ry} y={base} />
      <Guide x1={CX} x2={CX + radius} y1={base} y2={base} />
      <circle cx={CX} cy={base} fill={SHAPE.guide} r={3} />
      <Label anchor="start" x={CX + radius + 8} y={base + 4}>
        {label("radius")}
      </Label>
    </>
  );
}

function Pyramid({ label, value, slantLabel }: DrawProps) {
  const [l, w, h] = proportions([
    value("length"),
    value("width"),
    value("height"),
  ]);
  const k = Math.min(170 / (l + DEPTH_X * w), 125 / (h + DEPTH_Y * w));
  const fw = l * k;
  const ox = DEPTH_X * w * k;
  const oy = DEPTH_Y * w * k;
  const x0 = CX - (fw + ox) / 2;
  const yb = BOTTOM;
  const fl = { x: x0, y: yb };
  const fr = { x: x0 + fw, y: yb };
  const br = { x: x0 + fw + ox, y: yb - oy };
  const bl = { x: x0 + ox, y: yb - oy };
  const centre = { x: (fl.x + br.x) / 2, y: (fl.y + br.y) / 2 };
  const apex = { x: centre.x, y: centre.y - h * k };
  const frontMid = { x: (fl.x + fr.x) / 2, y: yb };
  return (
    <>
      <Hidden from={bl} to={fl} />
      <Hidden from={bl} to={br} />
      <Hidden from={bl} to={apex} />
      <Face points={[fl, fr, apex]} />
      <Face points={[fr, br, apex]} shade={1.6} />
      <Guide x1={apex.x} x2={centre.x} y1={apex.y} y2={centre.y} />
      <Label anchor="end" x={apex.x - 10} y={apex.y + 2}>
        {label("height")}
      </Label>
      <Label x={frontMid.x} y={yb + 20}>
        {label("length")}
      </Label>
      <Label
        anchor="start"
        x={(fr.x + br.x) / 2 + 8}
        y={(fr.y + br.y) / 2 + 16}
      >
        {label("width")}
      </Label>
      <Slant from={frontMid} text={slantLabel} to={apex} />
    </>
  );
}

function Frustum({ label, value, slantLabel }: DrawProps) {
  const big = value("bottomRadius");
  const small = value("topRadius");
  const height = value("height");
  const widest = Math.max(big, small);
  const { w, h } = fitBox((2 * widest) / height, 170, 115);
  const k = w / 2 / widest;
  const rx1 = big * k;
  const rx2 = small * k;
  const ry1 = rx1 * RIM;
  const ry2 = rx2 * RIM;
  const bottom = BOTTOM - ry1;
  const top = bottom - h;
  return (
    <>
      <path
        d={`M${CX - rx1} ${bottom} L${CX - rx2} ${top} L${CX + rx2} ${top} L${CX + rx1} ${bottom} A${rx1} ${ry1} 0 0 1 ${CX - rx1} ${bottom} Z`}
        fill={FILL}
        fillOpacity={FILL_OPACITY}
      />
      <Edge from={{ x: CX - rx1, y: bottom }} to={{ x: CX - rx2, y: top }} />
      <Edge from={{ x: CX + rx1, y: bottom }} to={{ x: CX + rx2, y: top }} />
      <Rim cx={CX} rx={rx1} ry={ry1} y={bottom} />
      <ellipse
        cx={CX}
        cy={top}
        fill={FILL}
        fillOpacity={FILL_OPACITY * 1.5}
        rx={rx2}
        ry={ry2}
        stroke={SHAPE.stroke}
        strokeWidth={2.2}
      />
      <Guide x1={CX} x2={CX} y1={top} y2={bottom} />
      <Guide x1={CX} x2={CX + rx1} y1={bottom} y2={bottom} />
      <Guide x1={CX} x2={CX + rx2} y1={top} y2={top} />
      <Label
        anchor="end"
        x={CX - (rx1 + rx2) / 2 - 10}
        y={(top + bottom) / 2 + 4}
      >
        {label("height")}
      </Label>
      <Label x={CX + rx1 / 2} y={bottom + ry1 + 16}>
        {label("bottomRadius")}
      </Label>
      <Label x={CX + rx2 / 2} y={top - ry2 - 6}>
        {label("topRadius")}
      </Label>
      <Slant
        from={{ x: CX + rx1, y: bottom }}
        text={slantLabel}
        to={{ x: CX + rx2, y: top }}
      />
    </>
  );
}

function Ellipsoid({ label, value }: DrawProps) {
  const [a, b, c] = proportions([
    value("semiA"),
    value("semiB"),
    value("semiC"),
  ]);
  const rx = a * 85;
  const ry = b * 70;
  const cy = 112;
  const depth = c * 40;
  return (
    <>
      <ellipse
        cx={CX}
        cy={cy}
        fill={FILL}
        fillOpacity={FILL_OPACITY}
        rx={rx}
        ry={ry}
        stroke={SHAPE.stroke}
        strokeWidth={2.2}
      />
      <Rim cx={CX} rx={rx} ry={Math.max(ry * 0.3, 8)} y={cy} />
      <Guide x1={CX} x2={CX + rx} y1={cy} y2={cy} />
      <Guide x1={CX} x2={CX} y1={cy} y2={cy - ry} />
      <Guide x1={CX} x2={CX - depth * 0.7} y1={cy} y2={cy + depth * 0.5} />
      <circle cx={CX} cy={cy} fill={SHAPE.guide} r={3} />
      <Label anchor="start" x={CX + rx + 8} y={cy + 4}>
        {label("semiA")}
      </Label>
      <Label anchor="start" x={CX + 8} y={cy - ry - 6}>
        {label("semiB")}
      </Label>
      <Label anchor="end" x={CX - depth * 0.7 - 6} y={cy + depth * 0.5 + 16}>
        {label("semiC")}
      </Label>
    </>
  );
}

const DRAWINGS: Record<SolidId, (props: DrawProps) => ReactNode> = {
  cube: (props) => <Box {...props} cube />,
  box: Box,
  triPrism: TriPrism,
  cylinder: Cylinder,
  cone: Cone,
  sphere: Sphere,
  hemisphere: Hemisphere,
  pyramid: Pyramid,
  frustum: Frustum,
  ellipsoid: Ellipsoid,
};

export function SolidDiagram(props: SolidDiagramProps) {
  const Draw = DRAWINGS[props.solid];
  const draw: DrawProps = {
    label: (id) => `${SOLID_SYMBOLS[id]} = ${props.labels[id] ?? ""}`,
    value: (id) => props.values[id] ?? 1,
    slantLabel: props.slantLabel,
  };
  return (
    <DiagramFrame svgRef={props.svgRef} title={props.title}>
      <Draw {...draw} />
    </DiagramFrame>
  );
}
